
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";

export interface PaymentStats {
  total: number;
  paid: number;
  pending: number;
  estimateCount: number;
  totalAmount: number;
  paidAmount: number;
  estimateAmount: number;
}

export interface DateRange {
  from: Date;
  to: Date;
}

export interface CustomerWithPayment {
  id: string;
  payment_status: string;
  amount?: number;
  currency?: string;
  description?: string;
}

export const usePaymentStats = (refreshTrigger: number, dateRange: DateRange) => {
  const { user } = useAuth();
  const [stats, setStats] = useState<PaymentStats | null>({
    total: 0,
    paid: 0,
    pending: 0,
    estimateCount: 0,
    totalAmount: 0,
    paidAmount: 0,
    estimateAmount: 0
  });
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Format dates to ensure we're comparing date-only values
      const fromDate = new Date(dateRange.from);
      fromDate.setHours(0, 0, 0, 0);
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);

      console.log("Dashboard: Fetching stats for date range:", {
        from: fromDate.toISOString(),
        to: toDate.toISOString(),
        originalFrom: dateRange.from,
        originalTo: dateRange.to
      });

      // Query with proper date filtering using DATE() function to avoid timezone issues
      const { data: customers, error: customersError } = await supabase
        .from('customers')
        .select(`
          *,
          payment_links:payment_link_id (
            amount,
            currency,
            description
          )
        `)
        .eq('user_id', user.id)
        .gte('created_at', fromDate.toISOString())
        .lte('created_at', toDate.toISOString());

      if (customersError) {
        console.error("Error fetching customers:", customersError);
        
        // Try fetching without date filter to see if data exists
        const { data: allCustomers, error: allCustomersError } = await supabase
          .from('customers')
          .select(`
            *,
            payment_links:payment_link_id (
              amount,
              currency,
              description
            )
          `)
          .eq('user_id', user.id);

        if (!allCustomersError) {
          console.log("Dashboard: All customers found (no date filter):", allCustomers?.length || 0);
          console.log("Dashboard: Sample customer dates:", allCustomers?.slice(0, 3).map(c => ({
            id: c.id,
            created_at: c.created_at,
            payment_status: c.payment_status
          })));
        }

        setStats({
          total: 0,
          paid: 0,
          pending: 0,
          estimateCount: 0,
          totalAmount: 0,
          paidAmount: 0,
          estimateAmount: 0
        });
        return;
      }

      console.log("Dashboard: Fetched customers with payment data:", customers?.length || 0);
      console.log("Dashboard: Sample customers:", customers?.slice(0, 3));

      const totalCustomers = customers?.length || 0;
      
      // Calculate stats based on customer payment status
      const paidCustomers = customers?.filter(customer => customer.payment_status === 'paid') || [];
      const pendingCustomers = customers?.filter(customer => 
        customer.payment_status === 'pending' || 
        customer.payment_status === 'overdue'
      ) || [];
      const estimateCustomers = customers?.filter(customer => customer.payment_status === 'estimate') || [];
      
      // Calculate total amount from ALL customers with payment amounts
      const totalAmount = customers?.reduce((sum, customer) => {
        if (customer.payment_links && customer.payment_links.amount) {
          return sum + Number(customer.payment_links.amount);
        }
        return sum;
      }, 0) || 0;
      
      // Calculate paid amount from only paid customers
      const paidAmount = paidCustomers.reduce((sum, customer) => {
        if (customer.payment_links && customer.payment_links.amount) {
          return sum + Number(customer.payment_links.amount);
        }
        return sum;
      }, 0);
      
      // Calculate estimate amount from only estimate customers
      const estimateAmount = estimateCustomers.reduce((sum, customer) => {
        if (customer.payment_links && customer.payment_links.amount) {
          return sum + Number(customer.payment_links.amount);
        }
        return sum;
      }, 0);
      
      console.log("Dashboard: Calculated stats:", {
        total: totalCustomers,
        paid: paidCustomers.length,
        pending: pendingCustomers.length,
        estimateCount: estimateCustomers.length,
        totalAmount: totalAmount,
        paidAmount: paidAmount,
        estimateAmount: estimateAmount,
        dateRange: `${fromDate.toDateString()} - ${toDate.toDateString()}`
      });
      
      setStats({
        total: totalCustomers,
        paid: paidCustomers.length,
        pending: pendingCustomers.length,
        estimateCount: estimateCustomers.length,
        totalAmount: totalAmount,
        paidAmount: paidAmount,
        estimateAmount: estimateAmount
      });
    } catch (error) {
      console.error("Error calculating stats:", error);
      setStats({
        total: 0,
        paid: 0,
        pending: 0,
        estimateCount: 0,
        totalAmount: 0,
        paidAmount: 0,
        estimateAmount: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [refreshTrigger, dateRange, user]);

  // Listen for customer status changes from CustomersList
  useEffect(() => {
    const handleCustomerStatusChange = () => {
      console.log("Dashboard: Customer status changed, refreshing stats...");
      fetchStats();
    };

    window.addEventListener('customerStatusChanged', handleCustomerStatusChange);

    return () => {
      window.removeEventListener('customerStatusChanged', handleCustomerStatusChange);
    };
  }, [dateRange, user]);

  return { stats, loading, fetchStats };
};
